import React, { useContext } from "react";
import { Form } from "react-final-form";
import arrayMutators from "final-form-arrays";
import { ArrowRight, ChevronLeft, Landmark, Loader2 } from "lucide-react";
import { IntlContext } from "context";
import formatErrorResponse from "helpers/form/formatErrorResponse";
import { TextField } from "components/Form/Field";
import Button from "ui/Button";
import { useRegisterStepper } from "../common/RegisterStepper";
import { useRegisterDraftGuard } from "../common/useRegisterDraftGuard";
import type { RegisterDraft } from "../types";
import { createRegisterProvider, validateRegisterProviderIban } from "hooks/register";
import { getErrorResponseData } from "helpers/typing";
import { trackRegisterStepComplete } from "lib/analytics/dataLayer";
import { STRIPE_CONNECTED_ACCOUNT_TOS_URL } from "config/externalLinks";
import { createStripeAccountToken } from "lib/stripe/createAccountToken";
import { fieldLabelWithTooltip } from "../common/FieldInfoTooltip";

const isValidFrenchIban = (ibanRaw: string) => {
    const iban = String(ibanRaw ?? "")
        .trim()
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, "");

    // FR + 2 check digits + 23 BBAN chars (27 total)
    if (!/^FR\d{2}\d{10}[A-Z0-9]{11}\d{2}$/.test(iban)) return false;

    // IBAN checksum (ISO 13616): move first 4 chars to end, convert letters A=10..Z=35, mod 97 must be 1.
    const rearranged = iban.slice(4) + iban.slice(0, 4);

    let remainder = 0;
    for (const ch of rearranged) {
        const expanded = ch >= "A" && ch <= "Z" ? String(ch.charCodeAt(0) - 55) : ch; // A->10 ... Z->35
        for (const digit of expanded) {
            remainder = (remainder * 10 + (digit.charCodeAt(0) - 48)) % 97;
        }
    }

    return remainder === 1;
};

interface Props {
    provider: RegisterDraft | undefined;
    setProvider: (provider: RegisterDraft | undefined) => void;
}

const RegisterProviderIban: React.FC<Props> = ({ provider, setProvider }) => {
    const intl = useContext(IntlContext);
    const stepper = useRegisterStepper();
    useRegisterDraftGuard(provider, "prestations");

    const onSubmit = async (values: RegisterDraft) => {
        try {
            const data = await validateRegisterProviderIban(values);
            const registrationPayload = { ...(data as HashMap<unknown>) };

            const publishableKey = process.env.REACT_APP_STRIPE_API_KEY_PUBLIC;
            if (publishableKey) {
                const draft = { ...(provider ?? {}), ...values };
                registrationPayload.stripe_account_token = await createStripeAccountToken(
                    publishableKey,
                    draft
                );
            }

            await createRegisterProvider(registrationPayload);
            trackRegisterStepComplete({
                registerType: "provider",
                stepKey: "iban",
                stepIndex: 7,
                stepLabel: intl.formatMessage("authentication.register.provider_flow.iban.label"),
            });
            stepper.goTo("confirmation");
        } catch (err: unknown) {
            const errData = getErrorResponseData(err);
            if (errData) {
                return formatErrorResponse(errData);
            }
            throw err;
        }
    };

    return (
        <React.Fragment>
            <Form
                onSubmit={onSubmit}
                initialValues={provider}
                mutators={{ ...arrayMutators }}
                validate={(values: RegisterDraft) => {
                    const errors: Record<string, string> = {};
                    const iban = String(values?.iban ?? "");

                    if (iban.trim() && !isValidFrenchIban(iban)) {
                        errors.iban = "IBAN français invalide.";
                    }

                    return errors;
                }}
                render={({ handleSubmit, submitting, values }) => (
                    <form onSubmit={handleSubmit} className="register__form">
                        <div className="flex flex--direction-column gap-10">
                            <p className="h1 font-black">
                                Où recevoir <span className="block text-primary">vos virements ?</span>
                            </p>
                            <p className="h4 margin-bottom--2 margin-top--1">
                                C'est très simple : nous avons uniquement besoin de vos coordonnées bancaires pour que notre plateforme de paiement sécurisée* puisse vous verser vos revenus automatiquement. Zéro paperasse, tout est automatisé pour vous simplifier la vie.
                            </p>

                            <div className="register__group-fields">
                                <div className="w-full rounded-[28px] bg-muted/30 p-6 sm:p-7">
                                    <div className="flex items-center gap-3">
                                        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-600">
                                            <Landmark className="h-5 w-5" />
                                        </div>
                                        <div>
                                            <div className="text-lg font-black">Virement bancaire</div>
                                            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                                                Compte de versement
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mt-6 space-y-5">
                                        <TextField
                                            inputProps={{
                                                name: "account_name",
                                                required: true,
                                                placeholder: "M. Jean Dupont",
                                                maxLength: 255,
                                                className:
                                                    "field-control__input h-14 rounded-[22px] border border-input bg-background px-6 text-sm placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring",
                                            }}
                                            labelProps={{
                                                label: "Nom complet du titulaire du compte",
                                                className:
                                                    "text-[11px] font-black uppercase tracking-wider text-foreground",
                                            }}
                                        />

                                        <TextField
                                            inputProps={{
                                                name: "iban",
                                                required: true,
                                                placeholder: "FR76 3000 6000 0112 3456 7890 123",
                                                maxLength: 34,
                                                className:
                                                    "field-control__input h-14 rounded-[22px] border border-input bg-background px-6 text-sm placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring",
                                            }}
                                            labelProps={{
                                                label: fieldLabelWithTooltip(
                                                    "IBAN (présent sur votre RIB)",
                                                    intl.formatMessage("register.label.provider.iban.tooltip"),
                                                ),
                                                className:
                                                    "text-[11px] font-black uppercase tracking-wider text-foreground",
                                            }}
                                        />

                                        <Button
                                            className="btn btn--primary w-full rounded-full p-3 justify-center gap-2"
                                            type="submit"
                                            disabled={
                                                submitting ||
                                                !String((values as RegisterDraft)?.account_name ?? "").trim() ||
                                                !String((values as RegisterDraft)?.iban ?? "").trim() ||
                                                !isValidFrenchIban(String((values as RegisterDraft)?.iban ?? ""))
                                            }
                                        >
                                            {submitting ? (
                                                <>
                                                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                                                    Création du compte en cours...
                                                </>
                                            ) : (
                                                <>
                                                    Valider et terminer
                                                    <ArrowRight className="w-4" />
                                                </>
                                            )}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                            <div className="text-xs text-muted-foreground">
                                * 🔒 Notre partenaire Stripe, leader mondial du paiement en ligne, assure la sécurité et la confidentialité de vos transactions. En cliquant sur "Valider et terminer", vous acceptez les <a href={STRIPE_CONNECTED_ACCOUNT_TOS_URL} target="_blank" className="text-primary" rel="noopener noreferrer">conditions d'utilisation</a> des comptes connectés Stripe.
                            </div>
                            <div className="flex items-center justify-between text-center margin-top--10">
                                <Button
                                    className="btn inline-flex items-center justify-center gap-3 rounded-full px-0 py-4 font-black text-muted-foreground/70"
                                    onClick={() => {
                                        setProvider(Object.assign({}, provider, values));
                                        stepper.goTo("cesu");
                                    }}
                                >
                                    <ChevronLeft className="h-4 w-4" />
                                    {intl.formatMessage("generic.step.previous")}
                                </Button>
                                <div />
                            </div>
                        </div>

                    </form>
                )}
            />
        </React.Fragment>
    );
};

export default RegisterProviderIban;
